import store from '@/store';
import i18n from '@/i18n';
import { apolloClient } from '@/apollo';

import { camelCase } from 'lodash-es';
import { config } from '@common/config';
import getFirstErrorCode from '@utils/getFirstErrorCode';
import { ErrorResponse } from '@apollo/client/link/error';
import type { Router } from 'vue-router';
import { getTracker } from '@/tracking/trackingPlugin';
import { ProjectImportEvent, TrackingProperty } from '@/tracking/trackingTypes';
import { getFileExtension } from '@utils/getFileExtension';
import { buildFileUploadForm } from '@utils/buildFileUploadForm';
import { xmlToJson } from '@utils/xmlToJson';
import { captureException } from '@sentry/vue';
import {
  CreateImportProjectPresignedUrlDocument,
  ImportProjectFromAtlprojDocument,
  ProjectImportDocument,
  ProjectImportProgressDocument,
  ProjectImportProgressSubscription,
  ProjectImportProgressSubscriptionVariables,
} from '@/graphql/operations.gen';
import { messages } from '@/i18n/locales/en';

type ProgressDetailKey = keyof (typeof messages)['projectImport']['status']['progressDetail'];

export const getMessageForImportError = (error: ErrorResponse, name: string) => {
  let description: string = '';

  const errorCode = getFirstErrorCode(error);

  switch (errorCode) {
    case 'UPLOAD_FILEEXTENSION': {
      description = i18n.global.t('projectImport.error.input.fileType', {
        allowedFileTypes: config.supportedImportFileEndings.join(', '),
      });
      break;
    }
    default: {
      description = i18n.global.t('projectImport.error.codeReference', { code: errorCode || 'UNKNOWN_ERROR' });
    }
  }

  return i18n.global.t('projectImport.error.message', { name, description });
};

const handleError = (error: ErrorResponse, id: string | number, name: string) => {
  store.dispatch('notification/remove', id);

  store.dispatch('notification/add', {
    message: getMessageForImportError(error, name),
    timeout: false,
  });
};

const handleDirectUploadError = (errorString: string, id: string | number, name: string) => {
  let error = null;
  let errorCode = null;
  // errorString will be XML if the request reached Spaces
  // but will be text if it didn't (e.g. CORS problem)
  if (errorString.indexOf('<') >= 0) {
    // handle Spaces error (has XML format)
    const errorJson = xmlToJson(errorString);
    error = errorJson.Error;
    errorCode = error.Code['#text'];
  } else {
    // handle other errors (e.g. CORS, CSP...)
    const errorSplit = errorString.split(':');
    error = {
      code: errorSplit.shift().trim(),
      message: errorSplit.pop()?.trim(),
    };
    errorCode = error.code;
  }

  store.dispatch('notification/remove', id);

  let description: string = '';
  switch (errorCode) {
    case 'EntityTooLarge': {
      description = i18n.global.t('projectImport.error.input.fileSize', { limit: config.maxFileSizeMb.atlprojImport });
      break;
    }
    default: {
      description = i18n.global.t('projectImport.error.codeReference', { code: errorCode || 'UNKNOWN_ERROR' });
      break;
    }
  }

  store.dispatch('notification/add', {
    message: i18n.global.t('projectImport.error.message', { name, description }),
    timeout: false,
  });
};

/**
 * Handles the result from both creating and resuming projecti import
 * Note: we are passing in the router instead of importing it to avoid a cyclical dependency
 */
export const handleProjectImportResult = (result, name: string, router: Router) => {
  if (result?.status === 'USERS_FOUND') {
    apolloClient.writeQuery({
      query: ProjectImportDocument,
      variables: {
        id: result.id,
      },
      data: { projectImport: result },
    });

    store.dispatch('notification/add', {
      message: i18n.global.t('projectImport.status.usersFound', { name }),
      timeout: false,
      hideCloseButton: true,
      actions: [
        {
          label: i18n.global.t('projectImport.action.handleUsers'),
          action: (closeFn) => {
            router.push({ name: 'projectImport', params: { id: result.id } });
            closeFn();
          },
        },
      ],
    });
  } else if (result?.status === 'IN_PROGRESS') {
    let detailStatus = i18n.global.t('projectImport.status.progressDetail.importBase');

    store.dispatch('notification/add', {
      message: i18n.global.t('projectImport.status.progress', { name, detailStatus }),
      timeout: false,
      spinner: true,
      id: result.id,
    });

    const subscriptionObserver = apolloClient
      .subscribe<ProjectImportProgressSubscription, ProjectImportProgressSubscriptionVariables>({
        query: ProjectImportProgressDocument,
        variables: { id: result.id },
      })
      .subscribe({
        next({ data }) {
          const progress = data.projectImportProgress;

          if (progress.status === 'COMPLETE') {
            subscriptionObserver.unsubscribe();

            store.dispatch('notification/remove', result.id);

            store.dispatch('notification/add', {
              message: i18n.global.t('projectImport.status.complete', { name }),
              actions: [
                {
                  label: i18n.global.t('team.invitation.goto'),
                  action: (closeFn) => {
                    router.push({ name: 'project', params: { projectId: progress.projectId } });
                    closeFn();
                  },
                },
              ],
            });
          } else {
            detailStatus = i18n.global.t(
              `projectImport.status.progressDetail.${camelCase(progress.status) as ProgressDetailKey}`,
              { ...progress }
            );

            store.dispatch('notification/update', {
              id: result.id,
              message: i18n.global.t('projectImport.status.progress', { name, detailStatus }),
            });
          }
        },
        error(error) {
          subscriptionObserver.unsubscribe();

          handleError(error, result.id, name);

          captureException(error);
        },
      });
  }
};

/**
 * Uploads a atlproj file and processes the result
 * Note: this function is not inside the ProjectOverviewTopBar component, because the component
 * might be destroyed when the upload finishes, which would break the access to $t, $router etc.
 * @param file
 * @param router The router is passed as an argument to avoid a cyclical dependency
 */
export const uploadFile = async (file: File, router: Router) => {
  const id = await store.dispatch('notification/add', {
    message: i18n.global.t('projectImport.status.uploading', { name: file.name }),
    spinner: true,
    timeout: false,
  });

  getTracker().track(ProjectImportEvent.ProjectImportStarted, {
    [TrackingProperty.ProjectType]: getFileExtension(file.name),
  });

  // get a pre-signed URL to directly upload the file to cloud storage
  const res = await apolloClient
    .mutate({
      mutation: CreateImportProjectPresignedUrlDocument,
      variables: {
        fileName: file.name,
        fileType: file.type,
      },
    })
    .catch((error) => handleError(error, id, file.name));

  // stop execution if there was an error or no response
  if (!res) {
    return;
  }

  const { presignedPost, fileKey, fileLocation } = res.data.createImportProjectPresignedUrl;

  // upload file using the pre-signed URL
  try {
    const uploadResponse = await fetch(presignedPost.url, {
      method: 'POST',
      body: buildFileUploadForm(presignedPost.fields, file),
    });
    if (!uploadResponse.ok) {
      handleDirectUploadError(await uploadResponse.text(), id, file.name);

      return;
    }
  } catch (err) {
    handleDirectUploadError(err.toString(), id, file.name);

    return;
  }

  const response = await apolloClient
    .mutate({
      mutation: ImportProjectFromAtlprojDocument,
      variables: {
        fileKey,
        fileName: file.name,
        fileLocation,
      },
    })
    .catch((error) => handleError(error, id, file.name));

  const result = response?.data.importProjectFromAtlproj;

  store.dispatch('notification/remove', id);

  handleProjectImportResult(result, file.name, router);
};
